/**
 * gameAudio/ambience.ts — looping scene-ambience bus. One ambience track at a
 * time plays at low gain underneath SFX + music, keyed by the current scene
 * (HomePage habitat or page route). PR-16 split from lib/gameAudio.ts.
 *
 * Cross-fade behaviour: when the kid navigates to a new scene we fade the old
 * track's gain to 0 over AMBIENCE_FADE seconds and fade the new one in over
 * the same window, so route changes never produce an abrupt cut.
 *
 * Audio-mute interplay: when the user mutes via setAudioEnabled(false) we
 * remember the last requested scene key in pendingAmbienceKey but stop the
 * source. On unmute (registerOnAudioUnlock) we restart the remembered scene
 * — so the kid always lands in the right bed.
 *
 * 调用关系：
 *   - App.tsx calls playSceneAmbience(key) on every route change.
 *   - _context.unlockAudio()/setAudioEnabled() fire the registered hooks
 *     below to start/stop the bed.
 *   - Manifest: lib/sceneAmbienceManifest.ts.
 *
 * 关键约束：
 *   - Don't call startAmbienceTrack directly from outside this file — it
 *     bypasses the pendingAmbienceKey bookkeeping and will leave mute-state
 *     inconsistent.
 *   - Missing manifest entries (no recording yet) silently fade the previous
 *     bed out and leave silence; music + SFX still carry the moment.
 */

import {
  SCENE_AMBIENCE_MANIFEST,
  type SceneAmbienceKey,
} from '../sceneAmbienceManifest';
import {
  ensureAudio,
  getAmbienceGain,
  getAudioEnabled,
  getCtx,
  registerOnAudioOff,
  registerOnAudioUnlock,
} from './_context';

// Seconds for cross-fade between scene ambiences. Long enough to feel
// musical, short enough that navigation doesn't drag.
const AMBIENCE_FADE = 0.7;

type AmbienceTrack = {
  key: SceneAmbienceKey;
  source: AudioBufferSourceNode;
  gain: GainNode;
};

const ambienceBufferCache = new Map<string, AudioBuffer>();
const ambienceLoading = new Map<string, Promise<AudioBuffer | null>>();
const ambienceFailed = new Set<string>();
let currentAmbience: AmbienceTrack | null = null;
let pendingAmbienceKey: SceneAmbienceKey | null = null;

async function loadAmbienceSample(path: string): Promise<AudioBuffer | null> {
  if (ambienceBufferCache.has(path)) return ambienceBufferCache.get(path) || null;
  if (ambienceFailed.has(path)) return null;
  const inflight = ambienceLoading.get(path);
  if (inflight) return inflight;
  const audio = ensureAudio();
  if (!audio) return null;
  const promise = (async () => {
    try {
      const res = await fetch(path);
      if (!res.ok) throw new Error(`status ${res.status}`);
      const arr = await res.arrayBuffer();
      const buf = await audio.decodeAudioData(arr.slice(0));
      ambienceBufferCache.set(path, buf);
      return buf;
    } catch {
      ambienceFailed.add(path);
      return null;
    } finally {
      ambienceLoading.delete(path);
    }
  })();
  ambienceLoading.set(path, promise);
  return promise;
}

function pickAmbiencePath(key: SceneAmbienceKey): string | null {
  const variants = SCENE_AMBIENCE_MANIFEST[key];
  if (!variants || !variants.length) return null;
  return variants[Math.floor(Math.random() * variants.length)];
}

function fadeOutAndStop(track: AmbienceTrack, dur = AMBIENCE_FADE) {
  const audio = getCtx();
  if (!audio) {
    try { track.source.stop(); } catch {}
    return;
  }
  const t = audio.currentTime;
  const g = track.gain.gain;
  try {
    g.cancelScheduledValues(t);
    g.setValueAtTime(g.value, t);
    g.linearRampToValueAtTime(0.0001, t + dur);
  } catch {}
  // Stop after fade finishes
  try { track.source.stop(t + dur + 0.05); } catch {}
}

function startAmbienceTrack(key: SceneAmbienceKey, buf: AudioBuffer) {
  const audio = ensureAudio();
  const ambience = getAmbienceGain();
  if (!audio || !ambience) return;
  // If a previous track exists, fade it out
  if (currentAmbience) fadeOutAndStop(currentAmbience);

  const source = audio.createBufferSource();
  source.buffer = buf;
  source.loop = true;
  const gain = audio.createGain();
  const t = audio.currentTime;
  gain.gain.setValueAtTime(0.0001, t);
  gain.gain.linearRampToValueAtTime(1.0, t + AMBIENCE_FADE);
  source.connect(gain);
  gain.connect(ambience);
  source.start();
  currentAmbience = { key, source, gain };
}

/**
 * Play (or cross-fade to) the looping ambience for a given scene key. Safe to
 * call on every route change — if the same key is already playing we no-op.
 * If the manifest has no clip for this key, we just fade out whatever was
 * playing and leave silence in its place.
 */
export function playSceneAmbience(key: SceneAmbienceKey | null) {
  if (typeof window === 'undefined') return;
  if (!getAudioEnabled()) {
    // Audio is muted — just remember the requested key and bail. When the
    // user re-enables audio we'll start it then.
    pendingAmbienceKey = key;
    if (currentAmbience) fadeOutAndStop(currentAmbience);
    currentAmbience = null;
    return;
  }
  pendingAmbienceKey = key;
  if (!key) {
    if (currentAmbience) fadeOutAndStop(currentAmbience);
    currentAmbience = null;
    return;
  }
  if (currentAmbience && currentAmbience.key === key) return;

  const path = pickAmbiencePath(key);
  if (!path) {
    if (currentAmbience) fadeOutAndStop(currentAmbience);
    currentAmbience = null;
    return;
  }
  const audio = ensureAudio();
  if (!audio) return;
  if (audio.state === 'suspended') void audio.resume();

  const cached = ambienceBufferCache.get(path);
  if (cached) {
    startAmbienceTrack(key, cached);
    return;
  }
  void loadAmbienceSample(path).then(buf => {
    // The user may have switched scenes again while we were loading — only
    // start if this key is still the latest requested.
    if (!buf) return;
    if (pendingAmbienceKey !== key) return;
    if (!getAudioEnabled()) return;
    if (currentAmbience && currentAmbience.key === key) return;
    startAmbienceTrack(key, buf);
  });
}

/** Fade out and tear down whatever ambience is currently playing. */
export function stopSceneAmbience() {
  pendingAmbienceKey = null;
  if (currentAmbience) fadeOutAndStop(currentAmbience);
  currentAmbience = null;
}

// Lifecycle hooks: when the master mute fires, stop the bed; when audio
// unlocks (first gesture / unmute), re-arm whatever scene the app last
// requested so the kid hears the right bed again.
registerOnAudioOff(() => {
  if (currentAmbience) fadeOutAndStop(currentAmbience);
  currentAmbience = null;
});
registerOnAudioUnlock(() => {
  if (pendingAmbienceKey && !currentAmbience) playSceneAmbience(pendingAmbienceKey);
});
